import { Box } from 'native-base';
import React, { useCallback, useEffect } from 'react';

import { CheckButton } from '~/components/check/check-button';
import { Color } from '~/components/color';
import { Text } from '~/components/text';
import { useStateCallback } from '~/hooks/state-callback';

import { FormInputProps } from './form-input';

type FormCheckGroupInputOption<T extends string | number> = { option: T; name: string };

type FormCheckGroupInputProps<T extends string | number> = Omit<FormInputProps<T>, 'onSetValue' | 'initialValue'> & {
    onSetValue: (value: T[]) => T[];
    options: FormCheckGroupInputOption<T>[];
    initialValue: T[] | null;
};

export function FormCheckGroupInput<T extends string | number>(props: FormCheckGroupInputProps<T>) {
    const { caption, disabled, options, onSetValue, initialValue } = props;
    const [selection, setSelection] = useStateCallback<T[]>([]);

    useEffect(() => {
        if (initialValue !== null) {
            setSelection(initialValue, () => {});
        }
    }, [initialValue, setSelection]);

    const onPress = useCallback(
        (option: T) => {
            const optionIndex = selection.indexOf(option);
            if (optionIndex > -1) {
                const modifiedSelection = [...selection];
                modifiedSelection.splice(optionIndex, 1);
                setSelection(modifiedSelection, newSelection => onSetValue?.(newSelection));
            } else {
                setSelection([...selection, option], newSelection => onSetValue?.(newSelection));
            }
        },
        [onSetValue, selection, setSelection]
    );

    return (
        <Box>
            {caption ? (
                <Text.INPUT_FIELD_TITLE style={{ color: disabled ? Color.TEXT_DISABLED : Color.ALMOST_BLACK }}>
                    {caption}
                </Text.INPUT_FIELD_TITLE>
            ) : null}
            <>
                {options.map(({ option, name }) => (
                    <CheckButton
                        style={{ marginBottom: 2 }}
                        type="checkbox"
                        key={option}
                        selected={selection.includes(option)}
                        label={name}
                        onPress={() => onPress(option)}
                    />
                ))}
            </>
        </Box>
    );
}

FormCheckGroupInput.displayName = 'FormCheckGroupInput';
